import {
    CentralRegulatorActionFailure,
    LoadCentralRegulators,
    LoadCentralRegulatorsFailure,
    LoadCentralRegulatorsSuccess,
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced,
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedFailure,
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedSuccess,
    ReloadRegulatorSuccess,
    RemoveCentralRegulatorAttachment,
    RemovePlatformLicenseActivationFromSelectedCentralRegulator,
    RemovePlatformLicenseActivationFromSelectedCentralRegulatorFailure,
    RemoveRegulatorNote,
    UpsertCentralRegulatorAddressFailure,
    UpsertCentralRegulatorPlatformLicenseActivation,
    UpsertCentralRegulatorPlatformLicenseActivationFailure,
    UpsertRegulator,
    UpsertRegulatorBankDetails,
    UpsertRegulatorBankDetailsFailure,
    UpsertRegulatorFailure,
    UpsertRegulatorNote,
    UpsertRegulatorSuccess
} from '../actions/central-regulators.actions';
import {
    CreateCentralRegulatorDeactivationSuccess,
    UpdateCentralRegulatorDeactivationSuccess,
    CancelCentralRegulatorDeactivationSuccess,
    RevertCentralRegulatorDeactivationSuccess
} from '../actions/central-regulator-deactivation.actions';
import { createReducer, createSelector, on } from '@ngrx/store';
import { AppState } from '../store';
import { CentralRegulatorDTO } from '@iota/iota-management-portal-v4-client';
import { CentralRegulatorDTO as InhCoreCentralRegulatorDTO } from '@iota/inh-core-v1-management-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import moment from 'moment/moment';
import { buildCentralRegulatorEntities } from '../entity-builder-utils';
import { sortByNameWithAlphabeticCharactersFirst } from 'src/app/shared/utils';

export interface CentralRegulatorState extends EntityState<InhCoreCentralRegulatorDTO> {
    loading: boolean;
    error: any;
    actionLoading: boolean;
    actionError: any;
    lastCreatedRegulatorId: string | null;

    // Full DTO of the regulator currently shown on the details page. Kept separately from the
    // entity adapter (which holds the stripped list response) so that a later list-endpoint
    // response cannot overwrite the audit fields, licenses, etc. fetched via ReloadRegulator.
    loadedRegulator: CentralRegulatorDTO | undefined;

    licenseActivationIdsThatHaveNotBeenInvoiced: string[];
}

function selectCentralRegulatorId(regulator: InhCoreCentralRegulatorDTO): string {
    return regulator.id;
}

function sortCentralRegulators(a: InhCoreCentralRegulatorDTO, b: InhCoreCentralRegulatorDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const regulatorAdapter: EntityAdapter<InhCoreCentralRegulatorDTO> = createEntityAdapter<InhCoreCentralRegulatorDTO>({
    selectId: selectCentralRegulatorId,
    sortComparer: sortCentralRegulators
});

export const initialState: CentralRegulatorState = regulatorAdapter.getInitialState({
    loading: true,
    error: null,
    actionLoading: false,
    actionError: undefined,
    lastCreatedRegulatorId: null,

    loadedRegulator: undefined,

    licenseActivationIdsThatHaveNotBeenInvoiced: []
});

export const centralRegulatorReducer = createReducer(
    initialState,
    on(LoadCentralRegulators, (state) => ({
        ...state,
        loading: true
    })),
    on(LoadCentralRegulatorsSuccess, (state, { regulators }) => ({
        ...regulatorAdapter.setAll(regulators || [], state),
        loading: false,
        error: null
    })),
    on(LoadCentralRegulatorsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
    })),
    on(UpsertRegulator, (state) => ({
        ...state,
        actionLoading: true
    })),
    on(UpsertRegulatorSuccess, (state, { regulator }) => ({
        ...state,
        actionLoading: false,
        error: null,
        lastCreatedRegulatorId: regulator.centralRegulatorId,
        loadedRegulator: regulator
    })),
    on(UpsertRegulatorFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        error
    })),
    on(ReloadRegulatorSuccess, (state, { regulator }) => ({
        ...state,
        actionLoading: false,
        error: null,
        loadedRegulator: regulator
    })),
    on(UpsertCentralRegulatorAddressFailure, (state, { error }) => ({
        ...state,
        error
    })),
    on(UpsertRegulatorBankDetails, (state) => ({
        ...state,
        actionLoading: true
    })),

    on(UpsertRegulatorBankDetailsFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        error
    })),
    on(UpsertRegulatorNote, (state) => ({
        ...state,
        actionLoading: true
    })),
    on(RemoveRegulatorNote, (state) => ({
        ...state,
        actionLoading: true
    })),

    on(CentralRegulatorActionFailure, (state, { error }) => ({ ...state, actionError: error, actionLoading: false })),

    /* Attachments */
    on(RemoveCentralRegulatorAttachment, (state) => ({ ...state, actionLoading: true })),

    /* Company Platform License Activation */
    on(
        UpsertCentralRegulatorPlatformLicenseActivation,
        RemovePlatformLicenseActivationFromSelectedCentralRegulator,
        LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced,
        (state) => ({
            ...state,
            actionLoading: true,
            actionError: undefined
        })
    ),
    on(
        UpsertCentralRegulatorPlatformLicenseActivationFailure,
        RemovePlatformLicenseActivationFromSelectedCentralRegulatorFailure,
        LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedFailure,
        (state, { error }) => ({
            ...state,
            actionLoading: false,
            actionError: error
        })
    ),
    on(
        LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedSuccess,
        (state, { licenseActivationIdsThatHaveNotBeenInvoiced }) => ({
            ...state,
            actionLoading: false,
            actionError: undefined,
            licenseActivationIdsThatHaveNotBeenInvoiced
        })
    ),

    /* Central Regulator Deactivation */
    on(
        CreateCentralRegulatorDeactivationSuccess,
        UpdateCentralRegulatorDeactivationSuccess,
        CancelCentralRegulatorDeactivationSuccess,
        RevertCentralRegulatorDeactivationSuccess,
        // These endpoints answer with the stripped InhCore DTO, so only the deactivation fields are refreshed.
        (state, { centralRegulator }) =>
            regulatorAdapter.updateOne(
                {
                    id: centralRegulator.id,
                    changes: {
                        isDeactivated: centralRegulator.isDeactivated,
                        scheduledOrExecutedCentralRegulatorDeactivation: centralRegulator.scheduledOrExecutedCentralRegulatorDeactivation
                    }
                },
                {
                    ...state,
                    actionLoading: false,
                    actionError: undefined
                }
            )
    )
);

const { selectAll } = regulatorAdapter.getSelectors();

export const centralRegulatorsState = (state: AppState) => state.centralRegulatorState;

const _centralRegulators = createSelector(centralRegulatorsState, selectAll);
export const allCentralRegulators = createSelector(_centralRegulators, (centralRegulators) =>
    buildCentralRegulatorEntities(centralRegulators)
);
export const centralRegulatorsLoading = createSelector(centralRegulatorsState, (state) => state.loading);
export const loadedCentralRegulator = createSelector(centralRegulatorsState, (state) => state.loadedRegulator);

export const centralRegulatorById = (regulatorId: string) =>
    createSelector(allCentralRegulators, (regulators) => regulators.find((r) => r.centralRegulatorId === regulatorId));

export const centralRegulatorsActionLoading = createSelector(centralRegulatorsState, (state) => state.actionLoading);
export const centralRegulatorsActionError = createSelector(centralRegulatorsState, (state) => state.actionError);
export const lastCreatedRegulatorId = createSelector(centralRegulatorsState, (state) => state.lastCreatedRegulatorId);

export const licenseActivationIdsOfCentralRegulatorThatHaveNotBeenInvoiced = createSelector(
    centralRegulatorsState,
    (state) => state.licenseActivationIdsThatHaveNotBeenInvoiced
);

export const centralRegulatorIdToNameMap = createSelector(
    allCentralRegulators,
    (centralRegulators) =>
        new Map(centralRegulators?.map((centralRegulator) => [centralRegulator.centralRegulatorId, centralRegulator.name]))
);
export const centralRegulatorIdToCentralRegulatorNumberMap = createSelector(
    allCentralRegulators,
    (centralRegulators) =>
        new Map(
            centralRegulators?.map((centralRegulator) => [centralRegulator.centralRegulatorId, centralRegulator.centralRegulatorNumber])
        )
);

export const centralRegulatorsSortedByName = createSelector(allCentralRegulators, (allCentralRegulators) =>
    sortByNameWithAlphabeticCharactersFirst(allCentralRegulators)
);
